'use client';

import { useState, type ReactElement } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { AgentChartKind, AgentChartRow } from '@/lib/api/types';
import { CHART_COLORS, formatCategory } from '@/lib/assistant/chart';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

/**
 * A chart the assistant asked for.
 *
 * The rows were copied server-side out of a read-only tool result and
 * projected to exactly `x_key` and `y_keys` — the model never supplies
 * numbers, so this component can plot what it is given without sanitising
 * values. It does not re-fetch anything: what is drawn is what was read when
 * the answer was written.
 *
 * `kind: 'table'` never draws a chart at all (no toggle either — there is
 * nothing to toggle to); the other three kinds default to the chart with a
 * table escape hatch, built on the repo's `Table` primitive rather than
 * hand-rolled markup. Degrades to a long, scrollable table rather than
 * refusing to render when a chart is unusually large (up to 500 rows, per the
 * client-side bounds) — see the 29-row `analytics_summary` capture in
 * `chart-block.test.tsx`.
 */
export function ChartBlock({
  kind,
  title,
  x_key,
  y_keys,
  rows,
}: {
  kind: AgentChartKind;
  title: string;
  x_key: string;
  y_keys: string[];
  rows: AgentChartRow[];
}) {
  const [asTable, setAsTable] = useState(false);

  if (rows.length === 0) {
    return (
      <figure className="rounded-lg border p-3 text-xs text-muted-foreground">
        {title && <figcaption className="mb-1 font-medium text-foreground">{title}</figcaption>}
        No data for that period.
      </figure>
    );
  }

  const data: (AgentChartRow & { __label: string })[] = rows.map((row) => ({
    ...row,
    __label: formatCategory(row[x_key]),
  }));
  const showTable = asTable || kind === 'table';

  let chart: ReactElement;
  if (kind === 'pie') {
    chart = (
      <PieChart>
        <Tooltip />
        <Pie data={data} dataKey={y_keys[0]} nameKey="__label" outerRadius={70}>
          {data.map((row, index) => (
            <Cell key={`${row.__label}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
          ))}
        </Pie>
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    );
  } else if (kind === 'bar') {
    chart = (
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
        <XAxis dataKey="__label" tick={{ fontSize: 10 }} minTickGap={16} />
        <YAxis tick={{ fontSize: 10 }} width={32} allowDecimals={false} />
        <Tooltip />
        {y_keys.map((key, index) => (
          <Bar key={key} dataKey={key} fill={CHART_COLORS[index % CHART_COLORS.length]} />
        ))}
      </BarChart>
    );
  } else {
    chart = (
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
        <XAxis dataKey="__label" tick={{ fontSize: 10 }} minTickGap={16} />
        <YAxis tick={{ fontSize: 10 }} width={32} allowDecimals={false} />
        <Tooltip />
        {y_keys.map((key, index) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={CHART_COLORS[index % CHART_COLORS.length]}
            dot={false}
            strokeWidth={2}
          />
        ))}
      </LineChart>
    );
  }

  return (
    <figure className="rounded-lg border p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        {title && <figcaption className="text-xs font-medium">{title}</figcaption>}
        {kind !== 'table' && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[11px]"
            onClick={() => setAsTable((current) => !current)}
          >
            {showTable ? 'View as chart' : 'View as table'}
          </Button>
        )}
      </div>

      {showTable ? (
        <div className="max-h-64 overflow-auto">
          <Table className="text-xs">
            <TableHeader className="sticky top-0 bg-background">
              <TableRow>
                <TableHead className="h-auto py-1 pr-2 text-left">{x_key}</TableHead>
                {y_keys.map((key) => (
                  <TableHead key={key} className="h-auto py-1 pr-2 text-right">
                    {key}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row, index) => (
                <TableRow key={`${row.__label}-${index}`}>
                  <TableCell className="py-1 pr-2 whitespace-normal">{row.__label}</TableCell>
                  {y_keys.map((key) => (
                    <TableCell key={key} className="py-1 pr-2 text-right tabular-nums">
                      {String(row[key] ?? '')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          {chart}
        </ResponsiveContainer>
      )}
    </figure>
  );
}
